import {
  PropsWithChildren,
  ReactElement,
  ReactNode,
  Suspense,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';

// eslint-disable-next-line @typescript-eslint/no-restricted-imports
import {
  CircularProgress,
  Dialog,
  DialogProps,
  Paper,
  PaperProps,
  makeStyles,
} from '@material-ui/core';

const useStyles = makeStyles({
  paper: {
    transition: 'max-width 225ms ease-in-out 0ms',
  },
  paperFullScreen: {
    paddingBottom: 0,
  },
});

export type State = {
  content: ReactElement | null;
  dialogProps?: Partial<DialogProps>;
  onClose?: () => void;
  disableBackdropClick?: boolean;
  stack: ReactElement[];
};

const defaultState: State = {
  content: null,
  dialogProps: {},
  onClose: undefined,
  disableBackdropClick: true,
  stack: [],
};

export type ContextType = {
  openDialog: (opts: Partial<State>) => void;
  closeDialog: (
    event?: unknown,
    reason?: 'backdropClick' | 'escapeKeyDown',
  ) => void;

  popDialog: () => void;
};

const EXIT_TIMEOUT = 300;

const context = createContext<ContextType | undefined>(undefined);

export const useDialog = (): ContextType => {
  const _context = useContext(context);

  if (!_context) {
    throw new Error('Missing `DialogProvider` wrapper.');
  }

  return _context;
};

const LocationListener = ({
  children,
  onChange,
}: PropsWithChildren<{ onChange: () => void }>) => {
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const mountedRef = useRef(false);

  useEffect(() => {
    if (!searchParams.get('step')) {
      if (mountedRef.current) {
        onChange();
      }
    }
  }, [location, searchParams.get('step')]);

  useEffect(() => {
    mountedRef.current = true;
  }, []);

  return <>{children}</>;
};

/**
 * A component that shows a CircularProgress after a delay of 200ms.
 * If the component unmounts before the delay, nothing is shown.
 */
export const DelayedCircularProgress = () => {
  const [showSpinner, setShowSpinner] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => {
      setShowSpinner(true);
    }, 200);

    return () => {
      clearTimeout(timer);
    };
  }, []);

  if (!showSpinner) {
    return null;
  }

  return <CircularProgress />;
};

const SuspensePaper = (props: PaperProps) => {
  return (
    <Suspense fallback={<DelayedCircularProgress />}>
      <Paper {...props} />
    </Suspense>
  );
};

export const DialogProvider = ({ children }: PropsWithChildren) => {
  const classes = useStyles();

  const [{ content, dialogProps, onClose, disableBackdropClick }, setState] =
    useState<State>(defaultState);

  // We use the closing value to avoid empty dialog content before the animation finishes
  const [closing, setClosing] = useState(false);

  const handleClose = useCallback(() => {
    if (!closing) {
      setClosing(true);
      if (onClose) {
        onClose();
      }
    }
  }, [onClose, closing]);

  const closeDialog = useCallback(
    (_event: unknown, reason?: 'backdropClick' | 'escapeKeyDown') => {
      if (reason === 'backdropClick' && disableBackdropClick) {
        return;
      }
      handleClose();
    },
    [handleClose, disableBackdropClick],
  );

  const openDialog = useCallback(
    (opts: Partial<State>) => {
      setClosing(false);
      setState({
        ...defaultState,
        ...opts,
      });
    },
    [handleClose, defaultState],
  );

  const popDialog = useCallback(() => {
    setState(state => {
      const stack = [...state.stack];
      const popped = stack.pop() ?? null;
      return {
        ...state,
        content: popped,
        stack,
      };
    });
  }, [setState]);

  const isOpen = useMemo(() => {
    return !!content && !closing;
  }, [content, closing]);

  useEffect(() => {
    // Reset to default state after dialog has closed
    let timeout: ReturnType<typeof setTimeout>;
    if (content && closing) {
      timeout = setTimeout(() => {
        setState(defaultState);
        setClosing(false);
      }, EXIT_TIMEOUT);
    }
    return () => {
      if (timeout) clearTimeout(timeout);
    };
  }, [content, closing]);

  return (
    <context.Provider
      value={{
        openDialog,
        closeDialog,
        popDialog,
      }}
    >
      <Dialog
        open={isOpen}
        onClose={closeDialog}
        disableEnforceFocus
        disableRestoreFocus
        fullWidth
        maxWidth="sm"
        classes={classes}
        transitionDuration={{
          exit: EXIT_TIMEOUT,
        }}
        PaperComponent={SuspensePaper}
        {...dialogProps}
      >
        {content && (
          <LocationListener onChange={handleClose}>{content}</LocationListener>
        )}
      </Dialog>

      {children}
    </context.Provider>
  );
};

export type MockDialogContextType = ContextType;

export type MockDialogProviderProps = {
  children: ReactNode;
  openDialog?: jest.Mock;
  closeDialog?: jest.Mock;
  popDialog?: jest.Mock;
};

export const MockDialogProvider = ({
  children,
  openDialog = jest.fn(),
  closeDialog = jest.fn(),
  popDialog = jest.fn(),
}: MockDialogProviderProps) => {
  const contextValue: MockDialogContextType = {
    openDialog,
    closeDialog,
    popDialog,
  };

  return <context.Provider value={contextValue}>{children}</context.Provider>;
};
